"use client";

import Button from "@/components/Button";
import IconCart from "@/components/icons/IconCart";
import LazyIcon from "@/components/icons/LazyIcon";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import LocalizationServiceability from "@/components/LocalizationServiceability";
import { MODAL_CONTENT } from "@/constants/modalContent";
import { useStickyBannerContext } from "@/context/AppContext";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import { parseMetadata } from "@/lib/parse-metadata";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { resolveXfinityClickOffUrl } from "@/lib/clickthrough-destination";
import { TAGULAR_ELEMENT } from "@/types/tagular";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { useRef, useState } from "react";
import { Tooltip } from "react-tippy";
import "react-tippy/dist/tippy.css";
import { ContentContainer } from "../ContentContainer";
import { resolveHeroOverlayBackgrounds } from "./backgrounds";
import type {
  AlignmentObject,
  AlignmentOption,
  HeroOverlayProps,
} from "./types";

const Modal = dynamic(() => import("@/components/Modal"), { ssr: false });

const ICON_MAP = {
  cart: IconCart,
} as const;

type CtaIconName = keyof typeof ICON_MAP | null;

type ResolvedAlignment = {
  desktop: AlignmentOption;
  tablet: AlignmentOption;
  mobile: AlignmentOption;
};

const resolveCtaIconName = (name?: string): CtaIconName => {
  if (name && name in ICON_MAP) return name as CtaIconName;
  return null;
};

const resolveAlignment = (
  alignmentObject?: AlignmentObject,
  fallbackAlignment?: ResolvedAlignment,
  defaults: { desktop: AlignmentOption; mobile: AlignmentOption } = {
    desktop: "left",
    mobile: "left",
  }
): ResolvedAlignment => {
  const desktop =
    alignmentObject?.desktop ?? fallbackAlignment?.desktop ?? defaults.desktop;
  const mobile =
    alignmentObject?.mobile ?? fallbackAlignment?.mobile ?? defaults.mobile;
  const tablet = alignmentObject?.tablet ?? desktop;

  return {
    desktop,
    tablet,
    mobile,
  };
};

const getTextAlignmentClasses = (alignment: ResolvedAlignment) => {
  return cn({
    "text-left": alignment.mobile === "left",
    "text-center": alignment.mobile === "center",
    "md:text-left": alignment.tablet === "left",
    "md:text-center": alignment.tablet === "center",
    "xl:text-left": alignment.desktop === "left",
    "xl:text-center": alignment.desktop === "center",
  });
};

const getContainerAlignmentClasses = (alignment: ResolvedAlignment) => {
  return cn({
    "mx-auto": alignment.mobile === "center",
    "md:mx-auto": alignment.tablet === "center",
    "md:mx-0": alignment.tablet === "left",
    "xl:mx-auto": alignment.desktop === "center",
    "xl:mx-0": alignment.desktop === "left",
  });
};

const getCtaAlignmentClasses = (alignment: ResolvedAlignment) => {
  return cn({
    "items-start": alignment.mobile === "left",
    "items-center": alignment.mobile === "center",
    "md:items-start": alignment.tablet === "left",
    "md:items-center": alignment.tablet === "center",
    "xl:items-start": alignment.desktop === "left",
    "xl:items-center": alignment.desktop === "center",
  });
};

const HeroOverlay: React.FC<HeroOverlayProps> = ({
  title,
  eyebrow,
  bulletPoints,
  description,
  pricing,
  primaryCtaRight,
  secondaryCtaRight,
  primaryCtaLeft,
  secondaryCtaLeft,
  backgroundImages,
  valueProps,
  legal,
  localizationServiceability,
  className = "",
}) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalContent, setActiveModalContent] = useState<string | null>(
    null
  );
  const { stickyBannerHeight } = useStickyBannerContext();
  const monarchMetadata = useMonarchMetadata();

  const titleCopy = title?.copy?.trim() || "";
  const titleFontSize = title?.fontSize ?? "md";
  const titleAlignment = title?.alignmentObject;
  const eyebrowCopy = eyebrow?.copy?.trim() || "";
  const eyebrowAlignment = eyebrow?.alignmentObject;

  const descriptionCopy = description?.copy?.trim() || "";
  const descriptionAlignment = description?.alignmentObject;
  const titleResolvedAlignment = resolveAlignment(titleAlignment);
  const eyebrowResolvedAlignment = resolveAlignment(
    eyebrowAlignment,
    titleResolvedAlignment
  );
  const descriptionResolvedAlignment = resolveAlignment(
    descriptionAlignment,
    titleResolvedAlignment
  );
  const legalCopyText = legal?.copy?.trim() || "";
  const legalTooltipText = legal?.tooltip?.trim() || "";
  const legalDesktopPosition = legal?.desktopPosition ?? "outsideOverlay";
  const legalTabletPosition = legal?.tabletPosition ?? "outsideOverlay";
  const legalMobilePosition = legal?.mobilePosition ?? "inBackground";
  const hasLegalCopy = !!legalCopyText;

  const bulletPointItems = (bulletPoints ?? [])
    .map((point) => point.trim())
    .filter(Boolean);
  const renderBulletPoints = (
    className?: string
  ): React.ReactElement | null => {
    return (
      <ul className={className}>
        {bulletPointItems.map((point, index) => (
          <li key={index} className="text-body3 flex items-start">
            <span className="mr-2 shrink-0 font-bold">•</span>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderLegalCopy = (className?: string): React.ReactElement => (
    <LegalDisclaimer
      disclaimers={legalCopyText}
      tooltip={legalTooltipText}
      variant="inline"
      textAlign="center"
      className={cn("text-center text-black", className)}
    />
  );

  const valuePropItems = (valueProps ?? [])
    .map((item) => ({
      icon: item.icon,
      copy: item.copy?.trim() ?? "",
    }))
    .filter((item) => item.icon && item.copy);
  const hasValueProps = valuePropItems.length > 0;

  const pricingEyebrow = pricing?.eyebrow?.trim() || "";
  const pricingPrice = pricing?.price?.trim() || "";
  const pricingPeriod = pricing?.period?.trim() || "";
  const pricingTooltipCopy = pricing?.tooltip?.copy?.trim() || "";

  useElementViewed(heroRef, {
    location: "hero",
    name: "HeroOverlay",
    text: titleCopy.replace(/<[^>]*>/g, ""),
  });

  const handlePrimaryClick = (cta: {
    copy: string;
    href: string;
    elementName?: string;
  }) => {
    fireElementClicked({
      actionOutcome: "sentToLocalize",
      elementType: "ctaButton",
      location: "hero",
      text: cta.copy,
      name: `ctaButton${cta.elementName ?? "HeroOverlay"}`,
      outboundUrl: cta.href,
      ...(monarchMetadata && { monarchMetadata }),
    } as TAGULAR_ELEMENT);
  };

  const handleSecondaryClick = (cta: {
    copy: string;
    action: string;
    elementName?: string;
  }) => {
    if (MODAL_CONTENT[cta.action]) {
      setActiveModalContent(cta.action);
      setIsModalOpen(true);
    }

    fireElementClicked({
      actionOutcome: "modalOpened",
      elementType: "ctaButton",
      location: "hero",
      text: cta.copy,
      name: cta.elementName ?? cta.action ?? "textLinkModal",
      ...(monarchMetadata && { monarchMetadata }),
    } as TAGULAR_ELEMENT);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveModalContent(null);
  };

  const hasPricing = !!(pricingPrice && pricingPeriod);

  const {
    desktop: desktopBackgroundImage,
    tablet: tabletBackgroundImage,
    mobile: mobileBackgroundImage,
    alt: backgroundImageAlt,
    hasBackgroundImages,
  } = resolveHeroOverlayBackgrounds(backgroundImages);
  const mobileImageTreatment =
    backgroundImages.mobileImageTreatment === "background"
      ? "background"
      : "lifestyle";
  const showMobileInlineBackgroundImage = mobileImageTreatment === "lifestyle";
  const showMobileFullBackgroundImage = mobileImageTreatment === "background";
  const hasLocalizationServiceability = Boolean(localizationServiceability);
  const localizationServiceabilityContent = localizationServiceability ? (
    <LocalizationServiceability {...localizationServiceability} />
  ) : null;

  const hasLeftPrimary = !!(
    primaryCtaLeft?.copy?.trim() && primaryCtaLeft?.href?.trim()
  );
  const hasLeftSecondary = !!(
    secondaryCtaLeft?.copy?.trim() && secondaryCtaLeft?.action?.trim()
  );
  const hasRightPrimary = !!(
    primaryCtaRight?.copy?.trim() && primaryCtaRight?.href?.trim()
  );
  const hasRightSecondary = !!(
    secondaryCtaRight?.copy?.trim() && secondaryCtaRight?.action?.trim()
  );
  const normalizedLeftPrimaryHref = resolveXfinityClickOffUrl(
    primaryCtaLeft?.href ?? ""
  );
  const normalizedRightPrimaryHref = resolveXfinityClickOffUrl(
    primaryCtaRight?.href ?? ""
  );
  const shouldRenderLeftCtas =
    !hasLocalizationServiceability && (hasLeftPrimary || hasLeftSecondary);
  const hasOverlayLeadingContent =
    !!eyebrowCopy ||
    !!titleCopy ||
    !!descriptionCopy ||
    bulletPointItems.length > 0 ||
    shouldRenderLeftCtas;

  const hasAnyContent =
    hasOverlayLeadingContent ||
    hasPricing ||
    hasLocalizationServiceability ||
    hasLeftPrimary ||
    hasLeftSecondary ||
    hasRightPrimary ||
    hasRightSecondary;

  return (
    <section className={cn("w-full overflow-hidden", className)}>
      <div className="w-full bg-purple-50 relative overflow-hidden flex flex-col justify-start items-center md:items-start py-6 md:px-8 md:pb-6 md:pt-12">
        {hasBackgroundImages ? (
          <>
            {showMobileFullBackgroundImage ? (
              <Image
                src={mobileBackgroundImage!}
                alt={backgroundImageAlt}
                fill
                sizes="100vw"
                className="md:hidden object-cover object-center"
                priority
              />
            ) : null}
            <Image
              src={tabletBackgroundImage!}
              alt={backgroundImageAlt}
              fill
              sizes="(max-width: 1279px) 0vw, 100vw"
              className="hidden md:block xl:hidden object-cover object-center"
              priority
            />
            <Image
              src={desktopBackgroundImage!}
              alt={backgroundImageAlt}
              fill
              sizes="(min-width: 1280px) 100vw"
              className="hidden xl:block object-cover object-center"
              priority
            />
          </>
        ) : null}

        <ContentContainer
          spacing="none"
          className="md:py-8 relative z-10"
          style={{
            paddingTop: stickyBannerHeight,
            transition: "padding-top 120ms ease-out",
          }}
        >
          <div
            className={`${hasPricing && hasLocalizationServiceability ? "md:w-166.75 xl:w-240" : hasLocalizationServiceability ? "md:w-157.5 xl:w-171" : hasPricing ? "md:w-154.5 xl:w-222.5" : "md:w-126 xl:w-150"} flex max-w-full flex-col gap-6 md:mx-0`}
          >
            {hasAnyContent ? (
              <div
                ref={heroRef}
                className={`
                md:bg-white/60
                md:backdrop-blur-[7.5px]
                md:rounded-lg
                md:shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.1),0px_20px_25px_-5px_rgba(0,0,0,0.1)]
                w-full
                ${hasLocalizationServiceability ? "md:overflow-visible" : "md:overflow-hidden"}
                ${hasPricing ? "md:px-8 md:py-6 xl:px-12 xl:py-10" : "md:p-8 xl:p-10"}
              `}
              >
                {hasPricing ? (
                  <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start md:items-center">
                    {hasOverlayLeadingContent ? (
                      <div
                        className={cn(
                          "flex w-full max-w-81.75 flex-col gap-2 md:max-w-98 md:gap-4 xl:max-w-141 xl:gap-6",
                          getContainerAlignmentClasses(titleResolvedAlignment)
                        )}
                      >
                        {eyebrowCopy ? (
                          <p
                            className={cn(
                              "title-sm-semibold text-theme1-base",
                              getTextAlignmentClasses(eyebrowResolvedAlignment)
                            )}
                            dangerouslySetInnerHTML={{
                              __html: parseMetadata(eyebrowCopy),
                            }}
                          />
                        ) : null}
                        {titleCopy ? (
                          <h1
                            className={cn(
                              {
                                "title-xl-bold md:title-1-5xl-bold":
                                  titleFontSize === "md",
                                "title-lg-bold md:title-2xl-bold":
                                  titleFontSize === "lg",
                                "title-lg-bold md:title-xl-bold xl:title-3xl-bold":
                                  titleFontSize === "xl",
                              },
                              getTextAlignmentClasses(titleResolvedAlignment)
                            )}
                            dangerouslySetInnerHTML={{
                              __html: parseMetadata(titleCopy),
                            }}
                          />
                        ) : null}
                        {descriptionCopy ? (
                          <p
                            className={cn(
                              "text-[16px] leading-5.5 tracking-[-0.02px] text-black md:text-[18px] md:leading-6.5 lg:text-[20px]",
                              getTextAlignmentClasses(
                                descriptionResolvedAlignment
                              )
                            )}
                          >
                            {descriptionCopy}
                          </p>
                        ) : null}

                        {bulletPointItems?.length > 0
                          ? renderBulletPoints()
                          : null}

                        {shouldRenderLeftCtas ? (
                          <div
                            className={cn(
                              "flex flex-col gap-4 w-full",
                              getCtaAlignmentClasses(titleResolvedAlignment)
                            )}
                          >
                            {hasLeftPrimary ? (
                              <Button
                                href={normalizedLeftPrimaryHref}
                                aria-label={primaryCtaLeft?.ariaLabel}
                                display="fill"
                                tone={primaryCtaLeft?.tone ?? "neutral-dark"}
                                size="sm"
                                className={cn(
                                  "inline-flex items-center justify-center gap-1 md:w-auto",
                                  {
                                    "w-full md:max-w-81.75":
                                      primaryCtaLeft?.mobileWidth === "full",
                                  }
                                )}
                                onClick={() =>
                                  handlePrimaryClick({
                                    copy: primaryCtaLeft!.copy!.trim(),
                                    href: normalizedLeftPrimaryHref,
                                    elementName: primaryCtaLeft!.elementName,
                                  })
                                }
                              >
                                {(() => {
                                  const foundIcon = resolveCtaIconName(
                                    primaryCtaLeft?.icon
                                  );
                                  if (!foundIcon) return null;

                                  const Icon = ICON_MAP[foundIcon];
                                  return (
                                    <Icon theme="white" className="h-6 w-6" />
                                  );
                                })()}
                                {primaryCtaLeft!.copy!.trim()}
                              </Button>
                            ) : null}

                            {hasLeftSecondary ? (
                              <button
                                onClick={() =>
                                  handleSecondaryClick({
                                    copy: secondaryCtaLeft!.copy!.trim(),
                                    action: secondaryCtaLeft!.action!.trim(),
                                    elementName: secondaryCtaLeft!.elementName,
                                  })
                                }
                                className="cursor-pointer text-[16px] leading-5.5 font-normal text-black underline decoration-solid"
                              >
                                {secondaryCtaLeft!.copy!.trim()}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {hasOverlayLeadingContent ? (
                      <div className="hidden w-px min-w-px max-w-px self-stretch bg-black/15 md:block" />
                    ) : null}

                    <div className="flex flex-col gap-2 items-center w-full md:w-auto shrink-0">
                      <div className="flex flex-col items-center md:gap-2 xl:gap-0">
                        {pricingEyebrow ? (
                          <p
                            className="w-full text-center title-base-semibold text-black md:text-left md:title-sm-semibold xl:title-base-semibold"
                            dangerouslySetInnerHTML={{
                              __html: parseMetadata(pricingEyebrow),
                            }}
                          />
                        ) : null}
                        <div className="flex items-end gap-1 pb-4 md:pb-2 xl:pb-4">
                          <div className="flex items-start">
                            <span className="pt-2 title-3xl-bold text-purple-500 md:pt-1 md:text-[36px] md:leading-10.5 md:tracking-[-0.04px] xl:pt-2 xl:title-3xl-bold">
                              $
                            </span>
                            <span className="price-point-700 text-purple-500 md:title-4xl-bold xl:price-point-700">
                              {pricingPrice}
                            </span>
                          </div>
                          <div className="flex flex-col pb-3 md:pb-3 xl:pb-3">
                            <span className="title-base-semibold text-purple-500 md:title-xs-semibold xl:title-base-semibold">
                              /mo for
                            </span>
                            <span className="title-base-semibold text-purple-500 md:title-xs-semibold xl:title-base-semibold">
                              {pricingPeriod}
                              {pricingTooltipCopy ? (
                                <Tooltip
                                  title={pricingTooltipCopy}
                                  position="top"
                                  trigger="mouseenter"
                                  arrow
                                  className="inline-flex"
                                >
                                  <button
                                    type="button"
                                    aria-label="Pricing info"
                                    className="ml-1 inline-flex items-center justify-center text-[12px] leading-none"
                                  >
                                    ⓘ
                                  </button>
                                </Tooltip>
                              ) : null}
                            </span>
                          </div>
                        </div>
                      </div>

                      {hasRightPrimary || hasRightSecondary ? (
                        <div className="flex flex-col gap-4 items-center w-full">
                          {hasRightPrimary ? (
                            <Button
                              href={normalizedRightPrimaryHref}
                              aria-label={primaryCtaRight?.ariaLabel}
                              display="fill"
                              tone="neutral-dark"
                              size="md"
                              className={cn(
                                "inline-flex items-center justify-center gap-1 md:w-auto",
                                {
                                  "w-full max-w-81.75":
                                    primaryCtaRight?.mobileWidth === "full",
                                }
                              )}
                              onClick={() =>
                                handlePrimaryClick({
                                  copy: primaryCtaRight!.copy!.trim(),
                                  href: normalizedRightPrimaryHref,
                                  elementName: primaryCtaRight!.elementName,
                                })
                              }
                            >
                              {(() => {
                                const foundIcon = resolveCtaIconName(
                                  primaryCtaRight?.icon
                                );
                                if (!foundIcon) return null;
                                const Icon = ICON_MAP[foundIcon];
                                return (
                                  <Icon
                                    theme="white"
                                    className="h-6 w-6"
                                    style="outline"
                                  />
                                );
                              })()}
                              {primaryCtaRight!.copy!.trim()}
                            </Button>
                          ) : null}

                          {hasRightSecondary ? (
                            <button
                              onClick={() =>
                                handleSecondaryClick({
                                  copy: secondaryCtaRight!.copy!.trim(),
                                  action: secondaryCtaRight!.action!.trim(),
                                  elementName: secondaryCtaRight!.elementName,
                                })
                              }
                              className="cursor-pointer text-[16px] leading-5.5 font-normal text-black underline decoration-solid"
                            >
                              {secondaryCtaRight!.copy!.trim()}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="w-full">
                    {eyebrowCopy ? (
                      <p
                        className={cn(
                          "title-xs-semibold md:title-sm-semibold text-theme1-base mb-2",
                          getTextAlignmentClasses(eyebrowResolvedAlignment)
                        )}
                        dangerouslySetInnerHTML={{
                          __html: parseMetadata(eyebrowCopy),
                        }}
                      />
                    ) : null}
                    {titleCopy ? (
                      <h1
                        className={cn(
                          "mb-4 md:mb-6",
                          {
                            "title-xl-bold md:title-1-5xl-bold":
                              titleFontSize === "md",
                            "title-lg-bold md:title-2xl-bold":
                              titleFontSize === "lg",
                            "title-xl-bold md:title-3xl-bold xl:title-3xl-bold":
                              titleFontSize === "xl",
                          },
                          getTextAlignmentClasses(titleResolvedAlignment)
                        )}
                        dangerouslySetInnerHTML={{
                          __html: parseMetadata(titleCopy),
                        }}
                      />
                    ) : null}

                    {descriptionCopy ? (
                      <p
                        className={cn(
                          "mb-6 text-[16px] leading-5.5 tracking-[-0.02px] text-black md:mb-8 md:text-[20px] md:leading-6.5",
                          getTextAlignmentClasses(descriptionResolvedAlignment)
                        )}
                      >
                        {descriptionCopy}
                      </p>
                    ) : null}

                    {bulletPointItems?.length > 0
                      ? renderBulletPoints("mb-6 md:mb-8")
                      : null}

                    {shouldRenderLeftCtas ? (
                      <div className="flex w-full flex-col items-center gap-4 md:flex-row md:gap-6">
                        {hasLeftPrimary ? (
                          <Button
                            href={normalizedLeftPrimaryHref}
                            aria-label={primaryCtaLeft?.ariaLabel}
                            display="fill"
                            tone={primaryCtaLeft?.tone ?? "neutral-dark"}
                            size="md"
                            className={cn(
                              "inline-flex items-center gap-2 md:w-auto",
                              {
                                "w-full md:max-w-81.75":
                                  primaryCtaLeft?.mobileWidth === "full",
                              }
                            )}
                            onClick={() =>
                              handlePrimaryClick({
                                copy: primaryCtaLeft!.copy!.trim(),
                                href: normalizedLeftPrimaryHref,
                                elementName: primaryCtaLeft!.elementName,
                              })
                            }
                          >
                            {(() => {
                              const foundIcon = resolveCtaIconName(
                                primaryCtaLeft?.icon
                              );
                              if (!foundIcon) return null;

                              const Icon = ICON_MAP[foundIcon];
                              return (
                                <Icon
                                  theme="white"
                                  className="h-6 w-6"
                                  style="outline"
                                />
                              );
                            })()}
                            {primaryCtaLeft!.copy!.trim()}
                          </Button>
                        ) : null}

                        {hasLeftSecondary ? (
                          <button
                            onClick={() =>
                              handleSecondaryClick({
                                copy: secondaryCtaLeft!.copy!.trim(),
                                action: secondaryCtaLeft!.action!.trim(),
                                elementName: secondaryCtaLeft!.elementName,
                              })
                            }
                            className="cursor-pointer text-[16px] leading-5.5 font-normal text-black underline decoration-solid"
                          >
                            {secondaryCtaLeft!.copy!.trim()}
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}

                {hasLocalizationServiceability ? (
                  <div className="md:mt-6 w-full">
                    {localizationServiceabilityContent}
                  </div>
                ) : null}

                {hasLegalCopy && legalTabletPosition === "inOverlay" ? (
                  <div className="mt-4 hidden md:block xl:hidden">
                    {renderLegalCopy()}
                  </div>
                ) : null}

                {hasLegalCopy && legalDesktopPosition === "inOverlay" ? (
                  <div className="mt-4 hidden xl:block">
                    {renderLegalCopy()}
                  </div>
                ) : null}
              </div>
            ) : null}
            {hasBackgroundImages && showMobileInlineBackgroundImage ? (
              <div className="w-full md:hidden">
                <div className="relative h-41 w-full overflow-hidden rounded-lg">
                  <Image
                    src={mobileBackgroundImage!}
                    alt={backgroundImageAlt}
                    fill
                    sizes="100vw"
                    className="object-cover"
                    priority
                  />
                </div>
              </div>
            ) : null}

            {hasValueProps ? (
              <div className="md:mt-2">
                <div className="mx-auto flex max-w-144.5 flex-col items-start gap-6 md:mx-0 md:flex-row md:items-center md:justify-between md:gap-8">
                  {valuePropItems.map((item) => (
                    <div
                      key={`${item.icon}-${item.copy}`}
                      className="flex w-full flex-row items-start gap-3 text-left md:w-auto md:flex-col md:items-center md:gap-4 md:text-center"
                    >
                      {item.icon ? (
                        <LazyIcon
                          name={item.icon}
                          className="h-6 w-6 shrink-0 md:h-8 md:w-8"
                          style="outline"
                          theme="purple"
                        />
                      ) : null}
                      <p className="title-xs-semibold text-neutral-base">
                        {item.copy}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {hasLegalCopy && legalTabletPosition === "outsideOverlay" ? (
              <div className="hidden md:block xl:hidden">
                {renderLegalCopy()}
              </div>
            ) : null}

            {hasLegalCopy && legalDesktopPosition === "outsideOverlay" ? (
              <div className="hidden xl:block">{renderLegalCopy()}</div>
            ) : null}

            {hasLegalCopy && legalMobilePosition === "inBackground" ? (
              <div className="md:hidden w-full">{renderLegalCopy()}</div>
            ) : null}
          </div>
        </ContentContainer>
      </div>

      {hasLegalCopy && legalTabletPosition === "outsideBackground" ? (
        <ContentContainer
          spacing="none"
          className="hidden md:block xl:hidden py-6"
        >
          {renderLegalCopy()}
        </ContentContainer>
      ) : null}

      {hasLegalCopy && legalDesktopPosition === "outsideBackground" ? (
        <ContentContainer spacing="none" className="hidden xl:block py-6">
          {renderLegalCopy()}
        </ContentContainer>
      ) : null}

      {hasLegalCopy && legalMobilePosition === "outsideBackground" ? (
        <ContentContainer spacing="none" className="md:hidden py-6">
          {renderLegalCopy()}
        </ContentContainer>
      ) : null}

      {activeModalContent && MODAL_CONTENT[activeModalContent] ? (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={MODAL_CONTENT[activeModalContent].title}
          actions={MODAL_CONTENT[activeModalContent].actions}
          tagularProps={{
            name: `modal${MODAL_CONTENT[activeModalContent].elementName}`,
          }}
        >
          {MODAL_CONTENT[activeModalContent].content}
        </Modal>
      ) : null}
    </section>
  );
};

export default HeroOverlay;
